import React, { useEffect, useState } from "react";
import { QuizFooter } from "./QuizFooter";
import { QuizShell } from "./shared/QuizShell";
import { QuizTabIndex } from "./shared/QuizTabProgress";
import { supportForQuestion, cutoutForQuestion } from "./shared/QuizDecor";
import StepBasic from "./steps/StepBasic";
import StepAbout from "./steps/StepAbout";
import StepTreats from "./steps/StepTreat";
import StepOwner from "./steps/StepOwner";
import StepDogsCount from "./steps/StepDogsCount";
import StepNames from "./steps/StepNames";

import { useQuiz } from "@/state/useQuiz";
import { useQuizContext } from "@/state/QuizContext";
import { useQuizNavigation } from "@/state/useQuizNavigation";
import { useSubStep } from "@/state/SubStepContext";
import { writeQuizStepToURL } from "@/state/urlState";
import { StepId } from "@/domains/quiz/StepDefinitions";
import { useScrollToTop } from "@/hooks/useScrollToTop";

export type QuizPhase = "count" | "names" | "questions";

const STEP_COMPONENTS: Partial<
  Record<
    StepId,
    React.ComponentType<{
      dogs: { id: string; form: any }[];
      updateDog: (dogId: string, update: any) => void;
    }>
  >
> = {
  basic: StepBasic,
  about: StepAbout,
  treat: StepTreats,
  owner: StepOwner,
};

/** Map the current global step onto the 4 macro tabs (My dog / Me / My plan / Summary). */
function tabForStep(stepId: StepId, phase: QuizPhase): QuizTabIndex {
  if (phase !== "questions") return 0;
  return stepId === "owner" ? 1 : 0; // basic/about/treat → My dog
}

export default function QuizStepper() {
  const { dogs, dogsCountConfirmed, setDogsCount } = useQuiz();
  const { dispatch } = useQuizContext();
  const { getDogStep } = useQuizNavigation();
  const { subStepInfo } = useSubStep();

  const firstDog = dogs[0] ?? null;

  const updateDog = (dogId: string, update: any) =>
    dispatch({ type: "UPDATE_DOG_FORM", dogId, payload: update });

  // All dogs move together; the first dog's step is canonical.
  const currentStepId: StepId = firstDog ? (getDogStep(firstDog.id) as StepId) : "basic";
  const StepComponent = STEP_COMPONENTS[currentStepId] || STEP_COMPONENTS["basic"];

  const allNamed = dogs.length > 0 && dogs.every((d) => !!d.form?.name?.trim());

  // Pre-question phases (Figma screens 1.0 + 19). Resume past them when already answered.
  const [phase, setPhase] = useState<QuizPhase>(() => {
    if (!dogsCountConfirmed) return "count";
    if (!allNamed) return "names";
    return "questions";
  });

  // Active question id drives the per-screen chrome (support block + dog cut-out).
  const questionId =
    phase !== "questions"
      ? null
      : currentStepId === "owner"
        ? "owner"
        : subStepInfo?.currentStep ?? null;

  // Scroll to top on every phase / step / sub-step change (next AND previous).
  useScrollToTop(`${phase}:${currentStepId}:${questionId}`);

  useEffect(() => {
    if (!firstDog) return;
    if (phase === "questions") writeQuizStepToURL(currentStepId, firstDog.id);
  }, [currentStepId, firstDog?.id, phase]);

  const activeTab = tabForStep(currentStepId, phase);
  const variant = phase === "count" || phase === "names" ? "hero" : "plain";

  return (
    <>
      <QuizShell
        activeTab={activeTab}
        variant={variant}
        support={supportForQuestion(questionId)}
        cutout={cutoutForQuestion(questionId)}
        onClose={() => {
          window.location.href = "/";
        }}
      >
        {phase === "count" ? (
          <StepDogsCount count={dogs.length} onSelect={setDogsCount} />
        ) : phase === "names" ? (
          <StepNames dogs={dogs} updateDog={updateDog} />
        ) : (
          StepComponent && firstDog && <StepComponent dogs={dogs} updateDog={updateDog} />
        )}
      </QuizShell>

      <QuizFooter phase={phase} setPhase={setPhase} />
    </>
  );
}